import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useAuth } from '../../providers/AuthProvider';
import { isValidConfirmationCodeFormat } from '../../services/auth-validation';
import { AppLogo } from '../AppLogo';

interface VerifyFormProps {
  /** The exact email captured at sign-up, used for confirm/resend (Req 2.3). */
  pendingEmail: string;
  /** Partially-masked email to display on the verify screen (Req 3.3). */
  destinationMasked?: string;
  /**
   * Called after a successful confirmation so the parent can return the User to
   * the sign-in view with a success indication (Req 2.4).
   */
  onConfirmed: () => void;
}

/**
 * Compute the whole seconds remaining until `until` (epoch ms), never negative.
 */
function secondsRemaining(until: number | null, now: number): number {
  if (until === null) {
    return 0;
  }
  return Math.max(0, Math.ceil((until - now) / 1000));
}

/**
 * Verification form: shows the pending (masked) email, accepts a 6-digit code
 * (format-validated inline, Req 2.4), submits via `confirmRegistration`, and
 * offers a "Resend code" control that is disabled while the cooldown window is
 * active — with a live countdown in its label (Req 3.1).
 *
 * Validates: Requirements 2.2, 2.4, 3.1, 10.5, 10.6.
 */
export function VerifyForm({
  pendingEmail,
  destinationMasked,
  onConfirmed,
}: VerifyFormProps) {
  const { confirmRegistration, resendCode, resendCooldownUntil, error } =
    useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  // Drives the live countdown re-render while a cooldown is active.
  const [now, setNow] = useState<number>(Date.now());

  const headingRef = useRef<HTMLHeadingElement>(null);

  // Place initial focus on the view heading when this view mounts (Req 10.6).
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // While a cooldown is pending, tick once per second so the countdown label
  // updates and the control re-enables when it elapses (Req 3.1).
  useEffect(() => {
    if (resendCooldownUntil === null) {
      return;
    }
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [resendCooldownUntil]);

  const remaining = secondsRemaining(resendCooldownUntil, now);
  const resendDisabled = remaining > 0;
  const codeValid = isValidConfirmationCodeFormat(code);
  const codeInvalid = code.length > 0 && !codeValid;
  const canSubmit = codeValid && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setLocalError(null);
    try {
      await confirmRegistration(pendingEmail, code);
      onConfirmed();
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : 'Verification failed.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendDisabled) {
      return;
    }
    setLocalError(null);
    try {
      await resendCode(pendingEmail);
    } catch (err) {
      setLocalError(
        err instanceof Error ? err.message : 'Could not resend the code.',
      );
    }
  };

  const shownError = localError ?? error;

  return (
    <form
      onSubmit={handleSubmit}
      style={{ width: '100%', maxWidth: '400px' }}
      aria-label="Verify email form"
    >
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
        <AppLogo size={56} />
      </div>
      <h1 ref={headingRef} className="focus-target" tabIndex={-1} style={{ marginBottom: '1rem' }}>
        Verify your email
      </h1>

      <p style={{ marginBottom: '1.5rem', color: 'var(--color-muted)' }}>
        We sent a 6-digit code to{' '}
        <strong style={{ color: 'var(--color-text)' }}>
          {destinationMasked ?? 'your email address'}
        </strong>
        . Enter it below to finish creating your account.
      </p>

      {shownError && (
        <div
          role="alert"
          style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}
        >
          {shownError}
        </div>
      )}

      <div style={{ marginBottom: '1.5rem' }}>
        <label
          htmlFor="verify-code"
          style={{ display: 'block', marginBottom: '0.25rem' }}
        >
          Verification code
        </label>
        <input
          id="verify-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          maxLength={6}
          required
          aria-invalid={codeInvalid}
          aria-describedby={codeInvalid ? 'verify-code-error' : undefined}
          style={{ width: '100%' }}
        />
        {codeInvalid && (
          <p
            id="verify-code-error"
            style={{
              color: 'var(--color-danger)',
              margin: '0.25rem 0 0',
              fontSize: '0.85rem',
            }}
          >
            Enter the 6-digit code.
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          width: '100%',
          padding: '0.75rem',
          backgroundColor: 'var(--color-brand)',
          color: 'var(--color-on-brand)',
          border: 'none',
          borderRadius: '4px',
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          fontSize: '1rem',
          opacity: canSubmit ? 1 : 0.7,
        }}
      >
        {submitting ? 'Verifying...' : 'Verify'}
      </button>

      <div style={{ marginTop: '1rem', textAlign: 'center' }}>
        <button
          type="button"
          onClick={handleResend}
          disabled={resendDisabled}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: resendDisabled ? 'var(--color-muted)' : 'var(--color-link)',
            cursor: resendDisabled ? 'not-allowed' : 'pointer',
            textDecoration: 'underline',
            fontSize: 'inherit',
          }}
        >
          {resendDisabled ? `Resend code (${remaining}s)` : 'Resend code'}
        </button>
      </div>
    </form>
  );
}
